import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Pencil } from "lucide-react";

const empty = {
  nome_fantasia: "",
  razao_social: "",
  cnpj: "",
  inscricao_estadual: "",
  regime_tributario: "simples",
  ambiente: "homologacao",
  active: true,
};

export default function Companies() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // null=closed, object=editing/creating

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/companies");
      setList(data);
    } catch (e) {
      toast.error("Erro ao listar");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (form.id) await api.patch(`/companies/${form.id}`, form);
      else await api.post("/companies", form);
      toast.success("Empresa salva");
      setForm(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao salvar");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir empresa?")) return;
    try {
      await api.delete(`/companies/${id}`);
      toast.success("Empresa excluída");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha");
    }
  };

  return (
    <div className="space-y-4" data-testid="companies-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Cadastros</div>
          <h1 className="text-3xl font-black tracking-tighter">Empresas</h1>
          <p className="text-sm text-slate-600 mt-1">Cadastro multiempresa ilimitado.</p>
        </div>
        <button
          data-testid="companies-new"
          onClick={() => setForm({ ...empty })}
          className="px-3 py-2 text-xs font-bold uppercase tracking-wider bg-[#002EA6] hover:bg-[#00207A] text-white flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> Nova empresa
        </button>
      </div>

      {form && (
        <div className="border border-slate-200 bg-white p-4 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="companies-form">
          <Field label="Nome fantasia" value={form.nome_fantasia} onChange={(v) => setForm({ ...form, nome_fantasia: v })} testid="company-nome-fantasia" />
          <Field label="Razão social" value={form.razao_social} onChange={(v) => setForm({ ...form, razao_social: v })} testid="company-razao" />
          <Field label="CNPJ" value={form.cnpj} onChange={(v) => setForm({ ...form, cnpj: v })} testid="company-cnpj" />
          <Field label="Inscrição estadual" value={form.inscricao_estadual} onChange={(v) => setForm({ ...form, inscricao_estadual: v })} testid="company-ie" />
          <Select label="Regime tributário" value={form.regime_tributario} onChange={(v) => setForm({ ...form, regime_tributario: v })} testid="company-regime"
            options={[["simples", "Simples Nacional"], ["lucro_presumido", "Lucro Presumido"], ["lucro_real", "Lucro Real"]]} />
          <Select label="Ambiente" value={form.ambiente} onChange={(v) => setForm({ ...form, ambiente: v })} testid="company-ambiente"
            options={[["homologacao", "Homologação"], ["producao", "Produção"]]} />
          <div className="md:col-span-3 flex justify-end gap-2 pt-2">
            <button onClick={() => setForm(null)} className="px-3 py-2 text-xs font-bold uppercase border border-slate-300 hover:border-slate-900">Cancelar</button>
            <button data-testid="company-save" onClick={save} className="px-4 py-2 text-xs font-bold uppercase bg-[#002EA6] hover:bg-[#00207A] text-white">Salvar</button>
          </div>
        </div>
      )}

      <div className="table-wrap">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-600">
            <tr>
              <th className="text-left px-3 py-2.5">Nome fantasia</th>
              <th className="text-left px-3 py-2.5">CNPJ</th>
              <th className="text-left px-3 py-2.5">Regime</th>
              <th className="text-left px-3 py-2.5">Ambiente</th>
              <th className="text-right px-3 py-2.5">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan="5" className="py-10 text-center"><Loader2 className="inline w-5 h-5 animate-spin text-slate-400" /></td></tr>)}
            {!loading && list.length === 0 && (<tr><td colSpan="5" className="py-10 text-center text-slate-500 text-sm">Nenhuma empresa cadastrada.</td></tr>)}
            {list.map((c) => (
              <tr key={c.id} className="t-row border-b border-slate-100" data-testid={`company-row-${c.cnpj}`}>
                <td className="px-3 py-2.5 font-semibold">{c.nome_fantasia}<div className="text-xs text-slate-500">{c.razao_social}</div></td>
                <td className="px-3 py-2.5 font-mono text-xs">{c.cnpj}</td>
                <td className="px-3 py-2.5 text-xs">{c.regime_tributario}</td>
                <td className="px-3 py-2.5">
                  <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase ${c.ambiente === "producao" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-700"}`}>{c.ambiente}</span>
                </td>
                <td className="px-3 py-2.5 text-right">
                  <button data-testid={`company-edit-${c.cnpj}`} onClick={() => setForm(c)} className="p-1.5 hover:bg-slate-200 text-slate-600 hover:text-slate-900"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`company-del-${c.cnpj}`} onClick={() => remove(c.id)} className="p-1.5 hover:bg-red-100 text-red-600"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-widest font-bold text-slate-700 mb-1">{label}</span>
      <input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-slate-300 rounded-sm px-2 py-2 text-sm focus:outline-none focus:border-[#002EA6]" />
    </label>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-widest font-bold text-slate-700 mb-1">{label}</span>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="w-full border border-slate-300 rounded-sm px-2 py-2 text-sm">
        {options.map(([v, l]) => (<option key={v} value={v}>{l}</option>))}
      </select>
    </label>
  );
}
